import React, { useState, useEffect } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Button, Space, Select, DatePicker, Spin } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const thStyle = {
  backgroundColor: "#daeffe",
  fontSize: "12px",
  fontWeight: "bold",
  padding: "8px 4px",
  border: "1px solid #e8e8e8",
  position: "sticky",
  top: 0,
  zIndex: 2,
  boxShadow: "0 1px 0 0 #e8e8e8",
};
const tdStyle = {
  padding: "6px 8px",
  border: "1px solid #e8e8e8",
  fontSize: "12px",
  color: "#333",
};

const { Option } = Select;
const { RangePicker } = DatePicker;

const PartyWiseMonthlySales = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [summary, setSummary] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [suffixOptions, setSuffixOptions] = useState(DEFAULT_SUFFIX_OPTIONS);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyName, setPartyName] = useState(undefined);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSOPartiesAndBrands`,
          { credentials: "include" },
        );
        const j = await res.json();
        if (!j.success) return;
        const parties = (j.parties || []).map((name) => ({
          label: name,
          value: name,
        }));
        setPartyOptions(parties);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [apiBaseUrl]);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchPartyData = async () => {
      setLoading(true);
      setData([]);
      setSummary({});
      try {
        const params = new URLSearchParams();
        params.set("financialYear", financialYear);
        if (partyName && String(partyName).trim()) {
          params.set("partyName", String(partyName).trim());
        }
        if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
          params.set("suffix", selectedSuffix.join(","));
        }
        if (dateRange && dateRange.length === 2) {
          params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
          params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
        }

        const url = `${apiBaseUrl}/inventoryManagement/getPartyWiseMonthlySales?${params.toString()}`;

        const response = await fetch(
          url,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          setData(result.data || []);
          setSummary(result.summary || {});

          const apiSuffixes =
            result.suffixOptions || result.suffixes || result.suffix || null;
          if (Array.isArray(apiSuffixes) && apiSuffixes.length > 0) {
            const opts = apiSuffixes
              .map((s) => (typeof s === "string" ? s : s?.value ?? s?.label))
              .map((s) => String(s ?? "").trim())
              .filter(Boolean);
            const uniq = Array.from(new Set(opts));
            setSuffixOptions(uniq.map((s) => ({ label: s, value: s })));
          } else {
            setSuffixOptions(DEFAULT_SUFFIX_OPTIONS);
          }
        } else {
          console.error("Failed to fetch party data:", result.message);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching party data:", error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchPartyData();
  }, [apiBaseUrl, financialYear, selectedSuffix, dateRange, partyName]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      if (partyName) {
        params.set("partyName", partyName);
      }
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      if (dateRange && dateRange.length === 2) {
        params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
        params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
      }
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getPartyWiseMonthlySales?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Party_Wise_Monthly_Sales_${financialYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const allMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const [visibleMonths, setVisibleMonths] = useState(allMonths);

  useEffect(() => {
    // If API sends monthlyTotals in summary, derive visible months from that
    if (summary?.monthlyTotals) {
      const summaryMonths = Object.keys(summary.monthlyTotals || {});
      const ordered = allMonths.filter((m) => summaryMonths.includes(m));
      if (ordered.length > 0) {
        setVisibleMonths(ordered);
        return;
      }
    }
    // Fallback: show all months
    setVisibleMonths(allMonths);
  }, [summary]);

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Party Wise Monthly Sales
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "12px 0",
                flexShrink: 0,
              }}
            >
              {/* Row 1: 3 filters */}
              <Space wrap size="middle">
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  allowClear
                  value={selectedSuffix.length ? selectedSuffix : undefined}
                  onChange={(v) => setSelectedSuffix(v ?? [])}
                  options={suffixOptions}
                  style={{ minWidth: 180 }}
                  dropdownRender={selectAllDropdownRender(
                    suffixOptions,
                    selectedSuffix,
                    setSelectedSuffix,
                  )}
                />
              </Space>

              {/* Row 2: date range + search (+ download) */}
              <Space wrap size="middle">
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD-MM-YYYY"
                  style={{ width: 220 }}
                  allowClear
                  placeholder={["Start Date", "End Date"]}
                />
                <Select
                  showSearch
                  allowClear
                  placeholder="Filter by Party Name"
                  value={partyName}
                  onChange={(v) => setPartyName(v)}
                  options={partyOptions}
                  style={{ width: 300 }}
                  filterOption={(input, opt) =>
                    String(opt?.label || "")
                      .toLowerCase()
                      .startsWith(String(input || "").toLowerCase())
                  }
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: 600 }}>
            <table
              style={{
                width: "100%",
                minWidth: 1400,
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      ...thStyle,
                      width: 260,
                      textAlign: "left",
                    }}
                  >
                    Party Name
                  </th>
                  {visibleMonths.map((month) => (
                    <th
                      key={month}
                      style={{ ...thStyle, width: 90, textAlign: "center" }}
                    >
                      {month.substr(0, 3)}
                    </th>
                  ))}
                  <th style={{ ...thStyle, width: 130, textAlign: "center" }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={(row.partyId || "") + (row.partyName || "") + idx}>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "left",
                        fontWeight: 700,
                      }}
                    >
                      {row.partyName || "N/A"}
                    </td>
                    {visibleMonths.map((month) => (
                      <td
                        key={month}
                        style={{ ...tdStyle, textAlign: "center" }}
                      >
                        {row?.monthlySales?.[month]
                          ? formatCurrency(row.monthlySales[month])
                          : "₹0"}
                      </td>
                    ))}
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "center",
                        fontWeight: 700,
                      }}
                    >
                      {row.totalSales ? formatCurrency(row.totalSales) : "₹0"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "left",
                      fontWeight: "bold",
                      backgroundColor: "#f0f8ff",
                    }}
                  >
                    Grand Total
                  </td>
                  {visibleMonths.map((month) => (
                    <td
                      key={month}
                      style={{
                        ...tdStyle,
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#f0f8ff",
                      }}
                    >
                      {summary?.monthlyTotals?.[month]
                        ? formatCurrency(summary.monthlyTotals[month])
                        : "₹0"}
                    </td>
                  ))}
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "center",
                      fontWeight: "bold",
                      backgroundColor: "#f0f8ff",
                    }}
                  >
                    {summary?.grandTotal ? formatCurrency(summary.grandTotal) : "₹0"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <style jsx>{`
        .total-row {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
        .total-row td {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
      `}</style>
    </div>
  );
};

export default PartyWiseMonthlySales;
